import React, { useState, useRef, useCallback, useMemo } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  EyeIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import FieldPalette from './FieldPalette';
import FormCanvas from './FormCanvas';
import FieldPropertiesPanel from './FieldPropertiesPanel';
import ConditionalLogicPanel from './ConditionalLogicPanel';
import FormSettingsPanel from './FormSettingsPanel';
import FormPreview from './FormPreview';

const INITIAL_FORM_SCHEMA = {
  version: "1.0",
  fields: []
};

const INITIAL_SETTINGS = {
  title: "New Form",
  description: "Form description",
  submit_button_text: "Submit",
  success_message: "Thank you! Your submission has been received.",
  allow_anonymous: true,
  auto_assign_team: "support",
  notification_email: true
};

const FormBuilder = ({ 
  initialTemplate = null, 
  onSave, 
  onCancel,
  readOnly = false 
}) => {
  // Form state
  const [formSchema, setFormSchema] = useState(
    initialTemplate?.form_schema || INITIAL_FORM_SCHEMA
  );
  const [conditionalRules, setConditionalRules] = useState(
    initialTemplate?.conditional_rules || []
  );
  const [formSettings, setFormSettings] = useState({
    ...INITIAL_SETTINGS,
    ...initialTemplate?.settings,
    name: initialTemplate?.name || INITIAL_SETTINGS.title,
    description: initialTemplate?.description || INITIAL_SETTINGS.description
  });

  // UI state
  const [selectedField, setSelectedField] = useState(null);
  const [activePanel, setActivePanel] = useState('fields'); // fields, logic, settings, preview
  const [draggedField, setDraggedField] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Refs
  const formCanvasRef = useRef(null);

  // Field management
  const addField = useCallback((fieldType, position = null) => {
    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: fieldType,
      label: getDefaultLabel(fieldType),
      required: false,
      ...getDefaultFieldProps(fieldType)
    };

    setFormSchema(prev => {
      const fields = [...prev.fields];
      if (position !== null && position >= 0 && position <= fields.length) {
        fields.splice(position, 0, newField);
      } else {
        fields.push(newField);
      }
      
      return { ...prev, fields };
    });

    setSelectedField(newField.id);
    setUnsavedChanges(true);
  }, []);

  const updateField = useCallback((fieldId, updates) => {
    setFormSchema(prev => ({
      ...prev,
      fields: prev.fields.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    }));
    setUnsavedChanges(true);
  }, []);

  const deleteField = useCallback((fieldId) => {
    setFormSchema(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.id !== fieldId)
    }));
    
    if (selectedField === fieldId) {
      setSelectedField(null);
    }
    
    // Remove any conditional rules that reference this field
    setConditionalRules(prev => 
      prev.filter(rule => 
        rule.condition?.field !== fieldId &&
        !rule.actions?.some(action => action.target === fieldId)
      )
    );
    
    setUnsavedChanges(true);
  }, [selectedField]);

  const duplicateField = useCallback((fieldId) => {
    const field = formSchema.fields.find(f => f.id === fieldId);
    if (!field) return;

    const duplicatedField = {
      ...field,
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      label: `${field.label} (Copy)`
    };

    const fieldIndex = formSchema.fields.findIndex(f => f.id === fieldId);
    
    setFormSchema(prev => {
      const fields = [...prev.fields];
      fields.splice(fieldIndex + 1, 0, duplicatedField);
      return { ...prev, fields };
    });

    setSelectedField(duplicatedField.id);
    setUnsavedChanges(true);
  }, [formSchema.fields]);

  const moveField = useCallback((fieldId, direction) => {
    const fieldIndex = formSchema.fields.findIndex(f => f.id === fieldId);
    if (fieldIndex === -1) return;

    const newIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
    if (newIndex < 0 || newIndex >= formSchema.fields.length) return;

    setFormSchema(prev => {
      const fields = [...prev.fields];
      const [movedField] = fields.splice(fieldIndex, 1);
      fields.splice(newIndex, 0, movedField);
      return { ...prev, fields };
    });

    setUnsavedChanges(true);
  }, [formSchema.fields]);

  // Conditional logic management
  const addConditionalRule = useCallback((rule) => {
    const newRule = {
      id: `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...rule
    };
    
    setConditionalRules(prev => [...prev, newRule]);
    setUnsavedChanges(true);
  }, []);

  const updateConditionalRule = useCallback((ruleId, updates) => {
    setConditionalRules(prev =>
      prev.map(rule => 
        rule.id === ruleId ? { ...rule, ...updates } : rule
      )
    );
    setUnsavedChanges(true);
  }, []);

  const deleteConditionalRule = useCallback((ruleId) => {
    setConditionalRules(prev => prev.filter(rule => rule.id !== ruleId));
    setUnsavedChanges(true);
  }, []);

  // Form saving
  const handleSave = useCallback(async () => {
    if (!onSave) return;

    const templateData = {
      name: formSettings.name,
      description: formSettings.description,
      category: formSettings.category || 'general',
      is_default: formSettings.is_default || false,
      form_schema: formSchema,
      conditional_rules: conditionalRules,
      settings: {
        submit_button_text: formSettings.submit_button_text,
        success_message: formSettings.success_message,
        allow_anonymous: formSettings.allow_anonymous,
        auto_assign_team: formSettings.auto_assign_team,
        notification_email: formSettings.notification_email
      }
    };

    try {
      await onSave(templateData);
      setUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save form:', error);
      // Handle error (show notification, etc.)
    }
  }, [formSchema, conditionalRules, formSettings, onSave]);

  // Panel tabs
  const panelTabs = [
    { id: 'fields', label: 'Fields', icon: Cog6ToothIcon },
    { id: 'logic', label: 'Logic', icon: BoltIcon, badge: conditionalRules.length },
    { id: 'settings', label: 'Settings', icon: Cog6ToothIcon },
    { id: 'preview', label: 'Preview', icon: EyeIcon }
  ];

  // Memoized components to prevent unnecessary re-renders
  const fieldPalette = useMemo(() => (
    <FieldPalette 
      onAddField={addField}
      disabled={readOnly}
    />
  ), [addField, readOnly]);

  const formCanvas = useMemo(() => (
    <FormCanvas
      ref={formCanvasRef}
      fields={formSchema.fields}
      selectedField={selectedField}
      onSelectField={setSelectedField}
      onUpdateField={updateField}
      onDeleteField={deleteField}
      onDuplicateField={duplicateField}
      onMoveField={moveField}
      onAddField={addField}
      readOnly={readOnly}
    />
  ), [
    formSchema.fields, 
    selectedField, 
    updateField, 
    deleteField, 
    duplicateField, 
    moveField, 
    addField, 
    readOnly
  ]);

  const renderActivePanel = () => {
    switch (activePanel) {
      case 'fields':
        return (
          <FieldPropertiesPanel
            field={selectedField ? formSchema.fields.find(f => f.id === selectedField) : null}
            allFields={formSchema.fields}
            onUpdateField={updateField}
            readOnly={readOnly}
          />
        );
      
      case 'logic':
        return (
          <ConditionalLogicPanel
            fields={formSchema.fields}
            rules={conditionalRules}
            onAddRule={addConditionalRule}
            onUpdateRule={updateConditionalRule}
            onDeleteRule={deleteConditionalRule}
            readOnly={readOnly}
          />
        );
      
      case 'settings':
        return (
          <FormSettingsPanel
            settings={formSettings}
            onUpdateSettings={(updates) => {
              setFormSettings(prev => ({ ...prev, ...updates }));
              setUnsavedChanges(true);
            }}
            readOnly={readOnly}
          />
        );
      
      case 'preview':
        return (
          <FormPreview
            schema={formSchema}
            rules={conditionalRules}
            settings={formSettings}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="form-builder h-full flex flex-col bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Form Builder
              </h2>
              {unsavedChanges && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200">
                  Unsaved Changes
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              {!readOnly && (
                <>
                  <button
                    onClick={handleSave}
                    disabled={!unsavedChanges}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Form
                  </button>
                  
                  {onCancel && (
                    <button
                      onClick={onCancel}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex min-h-0">
          {/* Left Panel - Field Palette */}
          <div className="w-64 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                Field Types
              </h3>
              {fieldPalette}
            </div>
          </div>

          {/* Center - Form Canvas */}
          <div className="flex-1 overflow-auto">
            <div className="p-6">
              <div className="max-w-2xl mx-auto">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 min-h-96">
                  <div className="p-6">
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {formSettings.name}
                      </h3>
                      {formSettings.description && (
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                          {formSettings.description}
                        </p>
                      )}
                    </div>
                    {formCanvas}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Properties/Settings */}
          <div className="w-80 flex-shrink-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
            {/* Panel Tabs */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-0">
                {panelTabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActivePanel(tab.id)}
                      className={`
                        flex-1 flex items-center justify-center px-3 py-3 text-sm font-medium border-b-2 transition-colors
                        ${activePanel === tab.id
                          ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                        }
                      `}
                    >
                      <Icon className="h-4 w-4 mr-1.5" />
                      {tab.label}
                      {tab.badge > 0 && (
                        <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-purple-500 rounded-full">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-auto p-4">
              {renderActivePanel()}
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

// Helper functions
const getDefaultLabel = (fieldType) => {
  const labels = {
    text: 'Text Input',
    textarea: 'Text Area',
    email: 'Email Address',
    phone: 'Phone Number',
    number: 'Number',
    date: 'Date',
    select: 'Dropdown',
    radio: 'Radio Buttons',
    checkbox: 'Checkboxes',
    file: 'File Upload',
    rating: 'Rating',
    slider: 'Slider',
    signature: 'Signature'
  };
  return labels[fieldType] || 'Field';
};

const getDefaultFieldProps = (fieldType) => {
  const defaults = {
    text: {
      placeholder: 'Enter text...',
      validation: { maxLength: 255 }
    },
    textarea: {
      placeholder: 'Enter your message...',
      settings: { rows: 4 },
      validation: { maxLength: 2000 }
    },
    email: {
      placeholder: 'Enter email address...'
    },
    phone: {
      placeholder: 'Enter phone number...'
    },
    number: {
      placeholder: 'Enter number...',
      validation: { min: 0 }
    },
    date: {},
    select: {
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' }
      ]
    },
    radio: {
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' }
      ]
    },
    checkbox: {
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' }
      ]
    },
    file: {
      validation: { 
        accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png',
        maxSize: '10MB'
      },
      settings: { multiple: false }
    },
    rating: {
      settings: { max: 5 }
    },
    slider: {
      validation: { min: 0, max: 100, step: 1 }
    },
    signature: {}
  };

  return defaults[fieldType] || {};
};

export default FormBuilder;